import { useState } from 'react'
import { useAuth } from '@/contexts/AuthProvider'
import { ArrowLeftRight, Check } from 'lucide-react'
import { cn } from '@/lib/utils'

export default function ProfileSwitcher() {
  const { profiles, userProfile, switchProfile, isSwitching } = useAuth()
  const [open, setOpen] = useState(false)

  if (!profiles || profiles.length <= 1) return null

  const otherProfiles = profiles.filter(p => p.id !== userProfile?.id)

  const handleSwitch = async (profileId) => {
    await switchProfile(profileId)
    setOpen(false)
  }

  return (
    <>
      {/* Trigger row — sits inside the nav links group */}
      <button
        onClick={() => setOpen(true)}
        className="w-full flex items-center justify-between px-4 py-3.5 text-sm text-gray-700 active:bg-gray-50 transition-colors"
      >
        <span className="flex items-center gap-3">
          <ArrowLeftRight className="h-4 w-4 text-gray-400" />
          Switch Profile
        </span>
        <div className="flex items-center gap-1.5">
          {otherProfiles.slice(0, 3).map(p => (
            <div
              key={p.id}
              className="h-6 w-6 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-medium text-gray-500"
            >
              {p.first_name?.[0]}{p.last_name?.[0]}
            </div>
          ))}
          {otherProfiles.length > 3 && (
            <span className="text-xs text-gray-400">+{otherProfiles.length - 3}</span>
          )}
        </div>
      </button>

      {/* Bottom sheet */}
      {open && (
        <>
          <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setOpen(false)} />
          <div className="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-2xl shadow-xl border-t border-gray-200 pb-safe animate-in slide-in-from-bottom duration-200">
            <div className="px-5 pt-4 pb-2">
              <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-4" />
              <p className="text-sm font-semibold text-gray-900">Switch Profile</p>
            </div>
            <div className="px-3 pb-4 space-y-1">
              {profiles.map(profile => {
                const isActive = profile.id === userProfile?.id
                return (
                  <button
                    key={profile.id}
                    onClick={() => !isActive && handleSwitch(profile.id)}
                    disabled={isSwitching || isActive}
                    className={cn(
                      'w-full text-left px-3 py-2.5 rounded-xl flex items-center gap-3 transition-colors',
                      isActive ? 'bg-emerald-50' : 'active:bg-gray-50'
                    )}
                  >
                    <div className={cn(
                      'h-9 w-9 rounded-full flex items-center justify-center text-sm font-medium shrink-0',
                      isActive ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600'
                    )}>
                      {profile.first_name?.[0]}{profile.last_name?.[0]}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {profile.first_name} {profile.last_name}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{profile.company?.name}</p>
                    </div>
                    {isActive && <Check className="h-4 w-4 text-emerald-600 shrink-0" />}
                  </button>
                )
              })}
            </div>
          </div>
        </>
      )}
    </>
  )
}
